import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getConfig } from '../config';
import type { Agent } from 'https';

export interface ApiClientOptionsI {
  baseURL?: string;
  headers?: Record<string, string>;
  proxy?: {
    host: string;
    port: number;
    protocol?: string;
  };
  httpsAgent?: Agent;
  auth?: {
    username: string;
    password: string;
  };
  bearer?: string; // For Bearer token authentication
}

export class ApiClient {
  private readonly axiosInstance: AxiosInstance;

  constructor(options: ApiClientOptionsI = {}) {
    const { baseURL, headers, proxy, httpsAgent, auth, bearer } = options;

    // Create Axios instance with provided options
    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        ...headers,
        ...(bearer && { Authorization: `Bearer ${bearer}` }), // Add Bearer token if provided
      },
      proxy: proxy
        ? {
            host: proxy.host,
            port: proxy.port,
            protocol: proxy.protocol ?? 'http',
          }
        : undefined,
      httpsAgent,
      auth, // Add basic auth if provided
    });
  }

  public setBearerToken(token: string | null) {
    if (token) {
      this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.axiosInstance.defaults.headers.common['Authorization'];
    }
  }

  // GET request
  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config);
  }

  // POST request
  public async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config);
  }

  // PUT request
  public async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config);
  }

  // DELETE request
  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config);
  }

  // PATCH request
  public async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config);
  }
}

const options: ApiClientOptionsI = {
  baseURL: getConfig().api.url,
  headers: {
    'Content-Type': 'application/json',
  },
};

export const apiClient = new ApiClient(options);
